import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * Route-level guard for global admin sections that VENUE_USER must not
 * reach: User Management, Wine Identity (Wines / Producers / Regions / Grapes
 * / Wine Names), Wine Governance, global Suppliers, global Inventory
 * Governance pages, etc.
 *
 * Backend gates these to [ADMIN, SYSTEM_ADMIN] already, so this is purely a
 * UX shield — without it a VENUE_USER who types /admin/wines into the URL
 * bar would get a chain of 403s and a half-rendered page.
 *
 * Redirect target for VENUE_USER:
 *   - 0 venues  → /unauthorized (data is misconfigured)
 *   - 1 venue   → that venue's dashboard
 *   - 2+ venues → /admin/my-venues
 */
const FullAdminGuard: React.FC = () => {
  const { user, isAdmin, isVenueUser } = useAuth();

  if (isAdmin()) return <Outlet />;

  if (isVenueUser()) {
    const assigned = user?.assignedVenueIds ?? [];
    if (assigned.length === 0) return <Navigate to="/unauthorized" replace />;
    if (assigned.length === 1) return <Navigate to={`/admin/venues/${assigned[0]}`} replace />;
    return <Navigate to="/admin/my-venues" replace />;
  }

  return <Navigate to="/unauthorized" replace />;
};

export default FullAdminGuard;
